import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtEur, fmtDate, BILLINGS, COMMITMENTS, STATUSES, downloadBlob } from "@/lib/api";

const downloadTemplate = () => downloadBlob("/admin/subscriptions/import-template", "modello_abbonamenti.csv");

function PreviewTable({ rows }) {
  return (
    <div className="max-h-[45vh] overflow-auto rounded-md border border-slate-800">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="w-12">Riga</TableHead><TableHead>Pagina</TableHead><TableHead>Servizio</TableHead>
            <TableHead className="text-right">Qtà</TableHead><TableHead className="text-right">P. unitario</TableHead><TableHead className="text-right">Totale</TableHead>
            <TableHead>Rinn./Fatt. auto</TableHead><TableHead>Fatt.</TableHead><TableHead>Obbligo</TableHead><TableHead>Rinnovo Servizio</TableHead><TableHead>Stato</TableHead><TableHead>Esito</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.row} className={`border-slate-800 ${r.errors.length ? "bg-rose-500/5" : ""}`} data-testid={`import-row-${r.row}`}>
              <TableCell className="font-mono text-slate-500">{r.row}</TableCell>
              <TableCell className="text-slate-300">{r.data.page_title}</TableCell>
              <TableCell className="font-medium">{r.data.service_name || <span className="text-slate-600">—</span>}</TableCell>
              <TableCell className="text-right font-mono">{r.data.quantity}</TableCell>
              <TableCell className="text-right font-mono text-slate-400">{fmtEur(r.data.unit_price)}</TableCell>
              <TableCell className="text-right font-mono">{fmtEur(r.data.price)}</TableCell>
              <TableCell className="text-slate-400">{r.data.auto_renew ? "Sì" : "No"}</TableCell>
              <TableCell className="text-slate-400">{BILLINGS[r.data.billing]}</TableCell>
              <TableCell className="text-amber-300">{COMMITMENTS[r.data.commitment]}</TableCell>
              <TableCell className="text-slate-400">{fmtDate(r.data.renewal_date)}</TableCell>
              <TableCell className="text-slate-400">{STATUSES[r.data.status]?.label}</TableCell>
              <TableCell>
                {r.errors.length
                  ? <span className="flex items-start gap-1 text-xs text-rose-400" data-testid={`import-row-${r.row}-errors`}><AlertTriangle size={13} className="mt-0.5 shrink-0" />{r.errors.join("; ")}</span>
                  : <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 size={13} />OK</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ImportSubscriptionsDialog({ clientId, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (dryRun) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dryRun ? "true" : "false");
    const { data } = await api.post(`/admin/clients/${clientId}/subscriptions/import`, fd);
    return data;
  };

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      setPreview(await send(true));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const data = await send(false);
      toast.success(`${data.imported} abbonamenti importati`);
      onImported();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl bg-[#111827] border-slate-800" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Importa abbonamenti da CSV</DialogTitle>
          <DialogDescription className="text-slate-400">
            Colonne: <span className="font-mono text-slate-300">pagina; servizio; descrizione; quantita; prezzo_unitario; rinnovo_automatico (si/no); fatturazione; obbligo (mensile/trimestrale/annuale/biennale/triennale); data_inizio; data_rinnovo; stato</span>.
            Il totale di riga viene calcolato (prezzo unitario × quantità).
            Separatore <span className="font-mono">;</span> o <span className="font-mono">,</span>, date GG/MM/AAAA, prezzo con virgola. La colonna <em>pagina</em> deve corrispondere al titolo di una pagina.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex-1 min-w-[260px] flex items-center gap-3 rounded-md border border-dashed border-slate-700 bg-slate-900/40 px-4 py-3 cursor-pointer hover:border-blue-500/50 transition-colors">
            <FileSpreadsheet size={20} className="text-blue-400 shrink-0" />
            <span className="text-sm text-slate-300 truncate" data-testid="import-file-name">{file ? file.name : "Scegli un file .csv"}</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }} data-testid="import-file-input" />
          </label>
          <Button variant="outline" onClick={downloadTemplate} className="border-slate-700 bg-transparent" data-testid="import-template-button"><Download size={15} className="mr-1.5" />Scarica modello</Button>
          <Button onClick={analyze} disabled={!file || busy} className="bg-blue-600 hover:bg-blue-500" data-testid="import-analyze-button">
            {busy && !preview ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}Analizza file
          </Button>
        </div>

        {preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm" data-testid="import-summary">
              <span className="text-emerald-400"><strong data-testid="import-valid-count">{preview.valid_count}</strong> righe valide</span>
              <span className={preview.error_count ? "text-rose-400" : "text-slate-500"}><strong data-testid="import-error-count">{preview.error_count}</strong> righe con errori (verranno ignorate)</span>
            </div>
            <PreviewTable rows={preview.rows} />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="import-cancel-button">Annulla</Button>
          <Button onClick={confirm} disabled={!preview || !preview.valid_count || busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid="import-confirm-button">
            {busy && preview ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}
            Importa {preview?.valid_count || 0} abbonamenti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
